import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, BadgeCheck, CircleCheck, Clock3 } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import ProcessFlow from "@/components/ProcessFlow";
import { Container, Eyebrow, Reveal, SectionHead } from "@/components/Motion";
import { IMAGES, OM_SERVICE } from "@/data/content";

export default function OmManpower() {
  return (
    <>
      <SEO title="O&M Manpower Services | EVOVLEX IT" description={OM_SERVICE.meta} />
      <PageHero
        eyebrow="Services — Operational & Workforce Solutions"
        titleLines={[
          "O&M Manpower",
          { node: <span className="text-gradient-brand">Services</span> },
        ]}
        sub={OM_SERVICE.tagline}
        image={IMAGES.omSolar}
      />

      <section data-testid="om-intro-section" className="bg-white py-20 lg:py-28">
        <Container>
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <SectionHead eyebrow="Overview" title="The Right People. The Right Skills. The Right Location." />
              <Reveal delay={0.2}>
                <p className="mt-6 text-base leading-relaxed text-slate-600 sm:text-lg">{OM_SERVICE.intro}</p>
              </Reveal>
              <Reveal delay={0.28}>
                <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">{OM_SERVICE.intro2}</p>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.15}>
                <div className="rounded-2xl border border-orange-500/20 bg-gradient-to-br from-orange-50 to-white p-8">
                  <Eyebrow>Manpower For</Eyebrow>
                  <ul className="mt-5 space-y-3">
                    {["Industrial Operations", "Infrastructure Projects", "Renewable Energy", "Electrical & Mechanical", "Facility Management"].map(
                      (t) => (
                        <li key={t} className="flex items-center gap-3 text-sm font-semibold text-slate-800">
                          <CircleCheck className="h-4 w-4 shrink-0 text-orange-500" />
                          {t}
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              </Reveal>
            </div>
          </div>
        </Container>
      </section>

      <section data-testid="om-categories-section" className="bg-slate-50 py-20 lg:py-28">
        <Container>
          <SectionHead
            eyebrow="Our O&M Manpower Services"
            title="Five Service Categories"
            sub="We provide manpower for various operational and maintenance activities."
          />
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {OM_SERVICE.categories.map((c, i) => (
              <Reveal key={c.title} delay={0.06 * (i % 3)} className={`h-full ${i === 3 ? "md:col-span-2 lg:col-span-1" : ""}`}>
                <div
                  data-testid={`om-service-category-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-orange-500/40 hover:shadow-xl hover:shadow-orange-500/5"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange-50 text-orange-500">
                    <c.icon className="h-6 w-6" strokeWidth={1.8} />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-semibold text-slate-900">{c.title}</h3>
                  {c.note && <p className="mt-2 text-xs italic text-slate-500">{c.note}</p>}
                  <ul className="mt-4 flex-1 space-y-2">
                    {c.roles.map((r) => (
                      <li key={r} className="flex items-start gap-2.5 text-sm text-slate-600">
                        <span className="mt-[7px] h-1 w-1 shrink-0 rotate-45 bg-orange-500" />
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <section data-testid="om-approach-section" className="relative overflow-hidden bg-navy-950 py-20 lg:py-28 grain">
        <div className="absolute inset-0 grid-lines-dark" aria-hidden="true" />
        <Container className="relative">
          <SectionHead
            dark
            eyebrow="Our O&M Approach"
            title="A Deployment Model Built for Continuity"
            sub={OM_SERVICE.approachIntro}
          />
          <div className="mt-14">
            <ProcessFlow steps={OM_SERVICE.approach} dark testid="om-detail-approach-process" />
          </div>
          <Reveal delay={0.2}>
            <p className="mt-16 font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-cyan-400">
              Key Activities
            </p>
          </Reveal>
          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {OM_SERVICE.keyActivities.map((a, i) => (
              <Reveal key={a} delay={0.03 * (i % 6)}>
                <div
                  data-testid={`om-activity-${a.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="flex items-center gap-3 rounded-lg border border-slate-800 bg-navy-900/60 px-4 py-3.5 transition-colors hover:border-cyan-500/40"
                >
                  <BadgeCheck className="h-4 w-4 shrink-0 text-cyan-400" strokeWidth={1.8} />
                  <span className="text-sm text-slate-300">{a}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <section data-testid="om-categories-table-section" className="bg-white py-20 lg:py-28">
        <Container>
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <SectionHead
                eyebrow="Manpower Categories"
                title="From Management to Site Support"
                sub="A complete spectrum of roles, deployable to match your operational structure."
              />
              <Reveal delay={0.25}>
                <div className="mt-8 overflow-hidden rounded-2xl border border-slate-800">
                  <img
                    src={IMAGES.omSolar}
                    alt="Solar plant operations and maintenance site"
                    className="aspect-[4/3] w-full object-cover"
                    loading="lazy"
                  />
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={0.1}>
                <div className="overflow-hidden rounded-2xl border border-slate-200" data-testid="om-manpower-table-roles">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-navy-950 text-white">
                        <th className="px-6 py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em]">Category</th>
                        <th className="px-6 py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em]">Manpower</th>
                      </tr>
                    </thead>
                    <tbody>
                      {OM_SERVICE.manpowerCategories.map((row, i) => (
                        <tr
                          key={row.category}
                          className={`${i % 2 === 0 ? "bg-white" : "bg-slate-50"} border-t border-slate-200 transition-colors hover:bg-blue-50/40`}
                        >
                          <td className="px-6 py-4 text-sm font-bold text-slate-900">{row.category}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">{row.roles}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Reveal>
            </div>
          </div>
        </Container>
      </section>

      <section data-testid="om-industries-section" className="bg-slate-50 py-20 lg:py-28">
        <Container>
          <SectionHead
            eyebrow="Industries We Serve"
            title="Manpower Solutions Across Sectors"
            sub="Our manpower solutions can support a wide range of operational environments."
          />
          <div className="mt-10 flex flex-wrap gap-3">
            {OM_SERVICE.industries.map((ind, i) => (
              <Reveal key={ind} delay={0.03 * (i % 8)}>
                <span
                  data-testid={`om-industry-${ind.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="inline-flex rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:border-orange-500/50 hover:text-orange-600"
                >
                  {ind}
                </span>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <section data-testid="om-benefits-section" className="bg-white py-20 lg:py-28">
        <Container>
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <SectionHead
                eyebrow="Client Benefits"
                title="What Outsourcing O&M Manpower Delivers"
                sub="By outsourcing O&M manpower requirements to EVOVLEX IT, clients can benefit from:"
              />
            </div>
            <div className="lg:col-span-8">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {OM_SERVICE.benefits.map((b, i) => (
                  <Reveal key={b} delay={0.04 * (i % 6)}>
                    <div
                      data-testid={`om-benefit-${b.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/60 px-5 py-4 transition-all duration-300 hover:border-cyan-500/40 hover:bg-white"
                    >
                      <CircleCheck className="h-5 w-5 shrink-0 text-evo-blue" strokeWidth={1.8} />
                      <span className="text-sm font-medium text-slate-800">{b}</span>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-20 grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <SectionHead
                eyebrow="Flexibility"
                title="Manpower That Matches Your Schedule"
                sub="Manpower can be provided based on project requirements."
              />
            </div>
            <div className="lg:col-span-8">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {OM_SERVICE.flexibility.map((f, i) => (
                  <Reveal key={f} delay={0.04 * (i % 4)}>
                    <div
                      data-testid={`om-flexibility-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="flex h-full flex-col items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-6 text-center transition-all duration-300 hover:border-orange-500/40 hover:shadow-lg"
                    >
                      <Clock3 className="h-5 w-5 text-orange-500" strokeWidth={1.8} />
                      <span className="text-xs font-bold uppercase tracking-wide text-slate-800">{f}</span>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </section>

      <section data-testid="om-why-section" className="bg-slate-50 py-20 lg:py-28">
        <Container>
          <SectionHead eyebrow="Why Choose Us" title="Built on Reliability" />
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {OM_SERVICE.whyChoose.map((w, i) => (
              <Reveal key={w.title} delay={0.06 * i} className="h-full">
                <div
                  data-testid={`om-why-${w.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="h-full rounded-2xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-orange-500/40 hover:shadow-xl"
                >
                  <span className="font-mono text-xs font-semibold tracking-widest text-orange-500">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{w.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500">{w.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <section data-testid="om-commitment-section" className="relative overflow-hidden bg-navy-950 py-20 lg:py-28 grain">
        <div className="absolute inset-0 grid-lines-dark" aria-hidden="true" />
        <div className="absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-orange-500/10 blur-[100px]" aria-hidden="true" />
        <Container className="relative">
          <div className="mx-auto max-w-3xl text-center">
            <Reveal>
              <Eyebrow dark className="justify-center">Our Commitment</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 font-display text-2xl font-semibold leading-snug text-white sm:text-3xl">
                "{OM_SERVICE.commitment.split(". ")[0]}."
              </p>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-6 text-base leading-relaxed text-slate-400">
                {OM_SERVICE.commitment.split(". ").slice(1).join(". ")}
              </p>
            </Reveal>
            <Reveal delay={0.26}>
              <div className="mt-8 flex flex-wrap justify-center gap-2.5">
                {["Productivity", "Safety", "Quality", "Operational Continuity"].map((t) => (
                  <span
                    key={t}
                    className="rounded-full border border-slate-700 px-4 py-2 font-mono text-xs uppercase tracking-widest text-cyan-300"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.34}>
              <Link
                to="/contact"
                data-testid="om-commitment-cta"
                className="mt-10 inline-flex items-center gap-2 rounded-lg bg-orange-500 px-7 py-4 text-sm font-semibold text-white shadow-lg shadow-orange-500/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-orange-600"
              >
                Discuss Your Manpower Requirements
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.4}>
            <p className="mt-14 text-center font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500">
              Reliable Workforce | Professional Operations | Sustainable Growth
            </p>
          </Reveal>
        </Container>
      </section>
    </>
  );
}
